// client/src/lib/format.ts — ro-RO display formatting (04 §9.9, NORMATIVE listing) · PROP-004.
//
// §9.9 prints this module in full and §7's rules lean on it — "these helpers are the only
// formatting path; `toLocaleString` calls scattered in components are a lint-flagged smell".
// It had no consumer until now, so it had never been written: PROP-004 is the first screen set
// that renders money, dates and percentages, and it writes the file rather than formatting
// inline. See DEVIATIONS D-361.
//
// The one rule that governs everything here: the client **never re-rounds money**. Amounts
// arrive at two decimals from the server (D8) and are FORMATTED, not computed. The single
// sanctioned exception is the investment panel's acquisition total (05 PROP-004 criterion 2),
// which is display-only, labelled as such on screen, and computed in `features/properties/
// rules.ts` — never here, so that this module stays incapable of inventing a number.

/** One `Intl.NumberFormat` per currency; constructing one per cell is the slow path. */
const moneyFormatters = new Map<string, Intl.NumberFormat>();

/**
 * `1234.56` → „1.234,56 RON". The currency is a CODE SUFFIX, not a symbol (§5.3): „lei" and
 * „€" both lose which of the two a multi-currency portfolio is looking at.
 */
export function formatMoney(amount: number, currency = 'RON'): string {
  let formatter = moneyFormatters.get(currency);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    moneyFormatters.set(currency, formatter);
  }
  return `${formatter.format(amount)} ${currency}`;
}

/** `formatMoney`'s figure without its currency — „2.500,00" — for a screen that sets the code apart. */
export function formatAmount(amount: number): string {
  return formatMoney(amount, '').trimEnd();
}

/**
 * `"2026-08-31"` → „31.08.2026".
 *
 * String slicing, never `new Date()`: a `DateOnly` has no time and no zone, and a Date
 * round-trip can shift a business date across midnight in either direction (§9.9, D8).
 */
export function formatDate(dateOnly: string): string {
  // Defence in depth, NOT the fix. The parameter stays `string` on purpose: a date the
  // server did not write is the CALLER's decision — „—", an omitted row — and a formatter
  // that swallowed it would render an empty cell where a dash belongs. What this line buys is
  // that a miss is a blank instead of a white screen, which is what four M1 screens showed
  // when `WhenWritingNull` handed this function `undefined` (DEVIATIONS D-238).
  if (typeof dateOnly !== 'string') return '';

  const [year, month, day] = dateOnly.split('-');
  if (year === undefined || month === undefined || day === undefined) return dateOnly;
  return `${day}.${month}.${year}`;
}

/**
 * `"2026-10"` or `"2026-10-05"` → „octombrie 2026" — the month heading SCR-FISC-002's agenda
 * groups under (04 §8.11) · FISC-007.
 *
 * §9.9 makes this module „the only formatting path" and calls a `toLocaleString` in a component
 * a smell, so the month names come from `Intl` here rather than from a twelve-string array in a
 * screen — which would also be twelve UI strings outside `romanian.json`.
 *
 * The `Date` is built from LOCAL parts, never parsed from the string: `new Date('2026-10-01')`
 * is UTC midnight, and in a browser west of Greenwich that is 30 September.
 */
export function formatMonthYear(yearMonth: string): string {
  if (typeof yearMonth !== 'string') return '';

  const [year, month] = yearMonth.split('-');
  if (year === undefined || month === undefined) return yearMonth;

  return new Intl.DateTimeFormat('ro-RO', { month: 'long', year: 'numeric' }).format(
    new Date(Number(year), Number(month) - 1, 1),
  );
}

/** A real instant — audit rows, notifications — rendered in the one timezone the app admits. */
export function formatInstant(iso: string): string {
  // As `formatDate`: strict in the type, tolerant at runtime. `new Date(undefined)` is an
  // Invalid Date and `Intl.DateTimeFormat.format` throws a RangeError on it, which is how the
  // PJ tenant profile went down on a column V1 never writes (D-238).
  if (typeof iso !== 'string') return '';

  return new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Bucharest',
  }).format(new Date(iso));
}

/**
 * A real instant rendered as a DATE in the one timezone the app admits — an expiry, a deadline,
 * anything a sentence reads „pe …" rather than „la …".
 *
 * Beside <see cref="formatInstant"/> rather than inside it, because the choice of whether the
 * time is part of the fact belongs to the caller: an audit row without its minute is a row you
 * cannot correlate, and an invitation expiry with one is noise (D-1592).
 */
export function formatInstantDate(iso: string): string {
  if (typeof iso !== 'string') return '';

  return new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'short',
    timeZone: 'Europe/Bucharest',
  }).format(new Date(iso));
}

/**
 * A real instant as a day and a short month — „21 oct." — in the one timezone the app admits.
 * For a fact read inside a sentence about the current month, where the year is understood: who
 * sent a clădire's meter index, and when (D-3683).
 */
export function formatInstantDayMonth(iso: string): string {
  if (typeof iso !== 'string') return '';

  return new Intl.DateTimeFormat('ro-RO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/Bucharest',
  }).format(new Date(iso));
}

/** `12.5` → „12,5%". Quotas and interest rates; one decimal is all either carries on screen. */
export const formatPercent = (value: number): string =>
  `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 1 }).format(value)}%`;

/**
 * `85.5` → „85,5 m²". Surfaces are `numeric(8,2)` (03 §7.2) and are a measurement rather than
 * money, so they keep their own helper and their own unit rather than borrowing `formatMoney`.
 */
export const formatSurface = (sqm: number): string =>
  `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 }).format(sqm)} m²`;

/**
 * `4.9746` → „4,9746". A BNR fixing, lei for one unit of another currency (D-4064).
 *
 * Four decimals at least, because that is how BNR publishes a rate and how a landlord checks it
 * against the bank's site; up to six, the scale `fiscal.exchange_rates.rate` stores a rate
 * quoted per hundred units at once it is divided down. Never `formatMoney`: a rate is not an
 * amount, and two decimals would print 4,97 for a fixing of 4,9746.
 */
export const formatExchangeRate = (rate: number): string =>
  new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(
    rate,
  );

/** `4215` → „4.215". A whole count a person reads, such as a text field's characters (D-3230). */
export const formatCount = (value: number): string =>
  new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(value);

/**
 * `12450` → „12,5 K"; `1250000` → „1,25 mil.". A chart AXIS tick, and nothing else.
 *
 * 04 §9.7 calls exactly this on the `YAxis` `tickFormatter` — „tooltips and axes use §9.9
 * formatters (never raw `toFixed`)" — and an axis is the one money surface where the full
 * „1.234.567,89 RON" of `formatMoney` does not fit and does not help: six ticks of it turn the
 * plot area into a margin at 360 px, which is the width §11.5 makes a hard gate.
 *
 * It carries NO currency, deliberately. A tick is read against a chart whose caption and legend
 * already name the currency once, and repeating „RON" six times down the axis is noise; every
 * figure a reader can act on is in the tooltip, where `formatMoney` writes it in full. See
 * DEVIATIONS D-733.
 */
export function formatCompactMoney(amount: number): string {
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat('ro-RO', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);
}

/** B → KB → MB → GB. The volume's own ceiling is 25 MB, so GB is a guard rather than a case. */
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/**
 * `1536` → „1,5 KB"; `26214400` → „25 MB". The size of an uploaded file, on the history list
 * and in the constraints line above the upload control (04 §5.5).
 *
 * A fifth helper in a module 04 §9.9 prints as four, for the reason `formatSurface` was a
 * fourth: §9.9's own rule is that these helpers are the ONLY formatting path, so a byte count
 * spelled inline in a component is the thing the rule forbids. See DEVIATIONS D-721.
 *
 * Binary steps (1024), because that is the base the 25 MB ceiling of 02 §9.3 is written in —
 * `FileStorageLimits.MaxContentBytes` is `25 * 1024 * 1024`, and a decimal formatter would
 * render the exact limit as „26,2 MB" under a control whose copy says 25.
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return `${String(bytes)} B`;

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }

  // One decimal only where it carries information: „1,5 MB" is worth saying, „1.536 B" is not,
  // and „24,0 MB" pretends to a precision the step size does not have.
  const maximumFractionDigits = unit === 0 || value >= 10 ? 0 : 1;

  return `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits }).format(value)} ${FILE_SIZE_UNITS[unit]!}`;
}

/**
 * `1234.5` + „kWh" → „1.234,5 kWh". A meter index and a consumption, which are measurements in
 * a unit the DATA carries — `UtilityService.measurementUnit` is `varchar(10)` free text, so the
 * unit is a parameter here where `formatSurface`'s „m²" is a constant.
 *
 * Three decimals, not two: `meter_readings.current_value` is `numeric(12,3)` (03 §11.3) and a
 * water meter's last digit is a litre. Trailing zeros are dropped, so „1.234 mc" and
 * „1.234,567 mc" are both read at the width they carry.
 *
 * A sixth helper in a module 04 §9.9 prints as four, for the reason `formatSurface` was a
 * fourth and `formatFileSize` a fifth (D-721): §9.9's own rule is that these helpers are the
 * ONLY formatting path, so an index spelled inline in a component is the thing the rule
 * forbids. See DEVIATIONS D-1129.
 */
export function formatQuantity(value: number, unit: string): string {
  if (!Number.isFinite(value)) return '';
  const formatted = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 3 }).format(value);
  return unit === '' ? formatted : `${formatted} ${unit}`;
}
